import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, TicketPercent } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "@/api";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
const SOURCE: Record<string, string> = {
  registration: "указан продавцом",
  link: "по ссылке",
  first_order: "первым заказом"
};

/**
 * «Промокод партнёра» магазина в консоли: кто привёл продавца. Привязка одна
 * на магазин; продавец указывает код сам (регистрация, анкета, профиль), а
 * привязать, перепривязать или снять код позже может только администратор —
 * с причиной, которая попадает в журнал действий.
 */
export function SellerPartnerPromo({
  userId
}: {
  userId: string;
}) {
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canEdit = hasAdminPermission(user, "action.promos.manage");
  const key = ["sellers", userId, "partner-promo"];
  const {
    data,
    error
  } = useQuery({
    queryKey: key,
    queryFn: () => api.promos.sellerBinding(userId)
  });
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const save = async (next: string | null) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.promos.bindSeller(userId, next, reason.trim() || undefined);
      await queryClient.invalidateQueries({
        queryKey: key
      });
      await queryClient.invalidateQueries({
        queryKey: ["promos"]
      });
      setEditing(false);
      setCode("");
      setReason("");
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return <Card data-testid="seller-partner-promo">
			<CardHeader className="flex flex-row items-center justify-between space-y-0">
				<CardTitle className="flex items-center gap-2 text-base">
					<TicketPercent className="size-4" />
					Промокод партнёра
				</CardTitle>
				{canEdit && !editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="seller-partner-promo-edit">
						{data?.bound ? "Перепривязать" : "Привязать"}
					</Button>}
			</CardHeader>
			<CardContent className="flex flex-col gap-3 text-sm">
				{error && <p className="text-destructive">{(error as Error).message}</p>}
				{data?.bound ? <div>
						<Link className="font-mono font-medium hover:underline" to={`/promos/${encodeURIComponent(data.bound.code)}`}>
							{data.bound.code}
						</Link>
						{data.bound.partnerName && <span> · партнёр {data.bound.partnerName}</span>}
						<div className="text-xs text-muted-foreground">
							{new Date(data.bound.boundAt).toLocaleDateString("ru-RU")}, {SOURCE[data.bound.source] ?? data.bound.source}
						</div>
					</div> : data && <p className="text-muted-foreground">
							Не указан.{" "}
							{data.canBind ? data.bindUntil ? `Продавец может указать сам до ${new Date(data.bindUntil).toLocaleDateString("ru-RU")}.` : "Продавец может указать сам, пока магазин не прошёл модерацию." : "Срок, когда продавец мог указать код сам, прошёл."}
						</p>}
				{editing && <div className="flex flex-col gap-2 rounded-md border p-3">
						<Input value={code} onChange={e_0 => setCode(e_0.target.value.toUpperCase().replace(/\s/g, ""))} placeholder="Продавцовый промокод" data-testid="seller-partner-promo-code" />
						<Input value={reason} onChange={e_1 => setReason(e_1.target.value)} placeholder="Причина (попадёт в журнал действий)" />
						{actionError && <p className="text-destructive">{actionError}</p>}
						<div className="flex flex-wrap gap-2">
							<Button size="sm" disabled={busy || !code.trim()} onClick={() => void save(code.trim())} data-testid="seller-partner-promo-save">
								{busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
								Сохранить
							</Button>
							{data?.bound && <Button size="sm" variant="outline" disabled={busy} onClick={() => void save(null)}>
									Снять привязку
								</Button>}
							<Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
								Отмена
							</Button>
						</div>
					</div>}
			</CardContent>
		</Card>;
}